import { useMemo } from 'react';

import { Box } from '@mui/material';
import { Edge, Node, Position, ReactFlow } from 'reactflow';

import 'reactflow/dist/style.css';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import CustomFlowNode, {
    CustomFlowNodeData,
} from 'domains/campaigns/components/CustomCampaign/GraphCampaignNavigation/CustomFlowNode';
import GraphNodeBlock from 'domains/campaigns/components/CustomCampaign/GraphCampaignNavigation/GraphNodeBlock';
import EndNode, { Type } from 'domains/campaigns/components/Graph/EndNode';
import { NodeType as GraphNodeEnumType } from 'domains/campaigns/components/Graph/types';
import { Campaign, CampaignGraph, SplitTypes } from 'domains/campaigns/types';
import buildCampaignGraph from 'domains/campaigns/utils/buildCampaignGraph';
import inferExitSendParentIds from 'domains/campaigns/utils/inferExitSendParentIds';
import synthesizeSplitNodeIds from 'domains/campaigns/utils/synthesizeSplitNodeIds';
import theme from 'theme';

const CUSTOM_FLOW_NODE = 'CUSTOM_FLOW_NODE';
const SPACING = 8;

const nodeTypes = {
    [GraphNodeEnumType.END_NODE]: EndNode,
    [CUSTOM_FLOW_NODE]: CustomFlowNode,
};

const edgeStyle = {
    animated: false,
    style: {
        stroke: theme.palette.action.active,
        strokeWidth: 2,
        strokeDasharray: 2,
    },
};

const splitEdgeLabelStyle = {
    labelBgBorderRadius: 4,
    labelShowBg: true,
    labelBgStyle: {
        fill: 'white',
        fillOpacity: 1,
        stroke: theme.palette.action.active,
        strokeWidth: 1,
    },
    labelStyle: {
        ...theme.typography.overline,
        fill: theme.palette.action.active,
    },
};

type NodeBranch = 'spine' | 'meets' | 'dnm';

const computeNodeBranches = ({ nodes, edges }: CampaignGraph): Map<string, NodeBranch> => {
    const branches = new Map<string, NodeBranch>();
    nodes.forEach((n) => branches.set(n.id, 'spine'));

    edges.forEach((e) => {
        if (e.label === 'meets') branches.set(e.target, 'meets');
        if (e.label === 'does_not_meet') branches.set(e.target, 'dnm');
    });
    nodes.forEach((n) => {
        if (n.data?.splitType === SplitTypes.MEETS) branches.set(n.id, 'meets');
        if (n.data?.splitType === SplitTypes.DOES_NOT_MEET) branches.set(n.id, 'dnm');
    });

    let changed = true;
    while (changed) {
        changed = false;
        for (const e of edges) {
            const src = branches.get(e.source);
            if (src !== 'spine' && branches.get(e.target) === 'spine') {
                branches.set(e.target, src);
                changed = true;
            }
        }
    }

    return branches;
};

const X_SPINE = SPACING * 40;
const X_MEETS = 0;
const X_END_SPLIT = Math.round((X_MEETS + X_SPINE) / 2);

const getX = (nodeId: string, branches: Map<string, NodeBranch>): number =>
    branches.get(nodeId) === 'meets' ? X_MEETS : X_SPINE;

const getY = (index: number): number => SPACING * (SPACING * 1.25 + index * 16);

export type Props = {
    campaign: Campaign;
    isPreviewOpen: boolean;
    hasNestedSplitsFlag?: boolean;
};

type VariantProps = {
    campaign: Campaign;
    isPreviewOpen: boolean;
};

// Legacy (flag OFF): flat list of GraphNodeBlock rows, with no nested-split awareness.
// This is the original behaviour shipped behind the campaignGraphNavigation flag.
const LegacyGraphCampaignDetailsNavigation = ({ campaign, isPreviewOpen }: VariantProps) => {
    const graph = useMemo(() => buildCampaignGraph(campaign.sends, undefined, campaign.abTestConfig), [
        campaign.sends,
        campaign.abTestConfig,
    ]);

    return (
        <Box
            data-testid="graph-campaign-details-navigation"
            role="navigation"
            aria-label="campaign workflow overview"
            display={isPreviewOpen ? 'none' : 'block'}
            ml={3}
            height="100%"
            width="75%"
        >
            {graph.nodes.map((node) => (
                <GraphNodeBlock
                    key={node.id}
                    node={node}
                    activeSendIndex={0}
                    activeStep={CustomCampaignSteps.NAME_CAMPAIGN}
                    readonly={true}
                />
            ))}
        </Box>
    );
};

// New (flag ON): readonly ReactFlow diagram matching the builder's GraphCampaignNavigation,
// with synthesized splitNodeIds so nested conditional splits render correctly.
const ReactFlowGraphCampaignDetailsNavigation = ({ campaign, isPreviewOpen }: VariantProps) => {
    const graph = useMemo(
        () =>
            buildCampaignGraph(
                synthesizeSplitNodeIds(inferExitSendParentIds(campaign.sends ?? [])),
                undefined,
                campaign.abTestConfig
            ),
        [campaign.sends, campaign.abTestConfig]
    );

    const branches = useMemo(() => computeNodeBranches(graph), [graph]);

    const nodes: Node<any>[] = useMemo(() => {
        const startNode: Node<any> = {
            id: 'flow-start',
            connectable: false,
            type: GraphNodeEnumType.END_NODE,
            data: { label: 'start', type: Type.START },
            position: { x: X_SPINE, y: 0 },
            sourcePosition: Position.Bottom,
        };

        const navigationNodes: Node<CustomFlowNodeData>[] = graph.nodes.map((node, index) => ({
            id: node.id,
            connectable: false,
            type: CUSTOM_FLOW_NODE,
            data: {
                ...node.data,
                nodeType: node.type,
                activeSendIndex: 0,
                activeStep: CustomCampaignSteps.NAME_CAMPAIGN,
                readonly: true,
                canRemove: false,
            },
            position: { x: getX(node.id, branches), y: getY(index) },
            sourcePosition: Position.Bottom,
            targetPosition: Position.Top,
        }));

        const sourcesWithChildrenSet = new Set(graph.edges.map((e) => e.source));
        const hasMeetsTerminal = graph.nodes.some(
            (n) => !sourcesWithChildrenSet.has(n.id) && branches.get(n.id) === 'meets'
        );
        const endNode: Node<any> = {
            id: 'flow-end',
            connectable: false,
            type: GraphNodeEnumType.END_NODE,
            data: { label: 'end', type: Type.END },
            position: { x: hasMeetsTerminal ? X_END_SPLIT : X_SPINE, y: getY(graph.nodes.length) },
            targetPosition: Position.Top,
        };

        return [startNode, ...navigationNodes, endNode];
    }, [branches, graph.nodes, graph.edges]);

    const edges: Edge<any>[] = useMemo(() => {
        const firstNodeId = graph.nodes[0]?.id;

        const edgeStart: Edge<any> = {
            ...edgeStyle,
            id: 'e-flow-start',
            source: 'flow-start',
            target: firstNodeId ?? 'flow-end',
            type: 'smoothstep',
            pathOptions: { borderRadius: 24 },
        };

        const navigationEdges: Edge<any>[] = graph.edges.map((edge) => {
            if (edge.label === 'meets') {
                return {
                    ...edgeStyle,
                    ...splitEdgeLabelStyle,
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    type: 'smoothstep',
                    label: 'meets',
                };
            }
            if (edge.label === 'does_not_meet') {
                return {
                    ...edgeStyle,
                    ...splitEdgeLabelStyle,
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    type: 'smoothstep',
                    label: 'does not meet',
                };
            }
            return {
                ...edgeStyle,
                id: edge.id,
                source: edge.source,
                target: edge.target,
                type: 'default',
            };
        });

        const sourcesWithChildren = new Set(graph.edges.map((e) => e.source));
        const terminalEdges: Edge<any>[] = graph.nodes
            .filter((n) => !sourcesWithChildren.has(n.id))
            .map((n) => ({
                ...edgeStyle,
                id: `e-${n.id}-flow-end`,
                source: n.id,
                target: 'flow-end',
                type: 'smoothstep',
                pathOptions: { borderRadius: 24 },
            }));

        return [edgeStart, ...navigationEdges, ...terminalEdges];
    }, [graph.nodes, graph.edges]);

    return (
        <Box
            data-testid="graph-campaign-details-navigation"
            role="navigation"
            aria-label="campaign workflow overview"
            display={isPreviewOpen ? 'none' : 'flex'}
            ml={3}
            height="100%"
            width="75%"
        >
            <ReactFlow
                nodeTypes={nodeTypes}
                nodes={nodes}
                edges={edges}
                maxZoom={1}
                minZoom={0.125}
                onInit={(instance) => instance.fitView({ duration: 500 })}
            />
        </Box>
    );
};

const GraphCampaignDetailsNavigation = ({ campaign, isPreviewOpen, hasNestedSplitsFlag = false }: Props) =>
    hasNestedSplitsFlag ? (
        <ReactFlowGraphCampaignDetailsNavigation campaign={campaign} isPreviewOpen={isPreviewOpen} />
    ) : (
        <LegacyGraphCampaignDetailsNavigation campaign={campaign} isPreviewOpen={isPreviewOpen} />
    );

export default GraphCampaignDetailsNavigation;
